import React, { useEffect, Suspense, useState } from "react";
import { Routes, Route, Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SecurityProvider } from "@/context/SecurityContext";
import { SecurityMonitoringProvider } from "@/context/SecurityMonitoringContext";
import { TestSecurityProvider } from "@/context/TestSecurityContext";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import LandingPage from "./pages/LandingPage";
import { Loader2 } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import CancellationPolicy from "./pages/CancellationPolicy";
import AboutUs from "./pages/AboutUs";
import ContactUs from "./pages/ContactUs";
import Pricing from "./pages/Pricing";
import CookiePolicy from "./pages/CookiePolicy";
import AccessibilityStatement from "./pages/AccessibilityStatement";
import AcceptableUsePolicy from "./pages/AcceptableUsePolicy";
import GDPRCompliance from "./pages/GDPRCompliance";
import DataProcessingAgreement from "./pages/DataProcessingAgreement";
import WordMockTestGeneratorPage from "./pages/WordMockTestGeneratorPage";
import PYQMultiplierPage from "./pages/PYQMultiplierPage";
import Dashboard from "./components/Dashboard";
import Quiz from "./components/Quiz";
import QuizHistory from "./components/quiz/QuizHistory";
import QuizHistoryDetail from "./components/quiz/QuizHistoryDetail";
import Analytics from "./components/analytics/Analytics";
import LearningPathVisualization from "./components/learning/LearningPathVisualization";
import FlashcardsPage from "./components/flashcards/FlashcardsPage";
import CurrentAffairsQuiz from "./components/current-affairs/CurrentAffairsQuiz";
import MockTestGenerator from "./components/mock-test/MockTestGenerator";
import MockTest from "./components/mock-test/MockTest";
import MockTestResults from "./components/mock-test/MockTestResults";
import MockTestAnalytics from "./components/mock-test/MockTestAnalytics";
import AdaptiveCurrentAffairsQuiz from "./components/current-affairs/AdaptiveCurrentAffairsQuiz";
import CurrentAffairs from "./components/current-affairs/CurrentAffairs";
import CurrentAffairsQuizResultDetail from "./components/current-affairs/CurrentAffairsQuizResultDetail";
import CurrentAffairsAnalytics from "./components/current-affairs/CurrentAffairsAnalytics";
import CurrentAffairsQuizResult from "./components/current-affairs/CurrentAffairsQuizResult";
import StudyNotes from "./components/notes/StudyNotes";
import Settings from "./pages/Settings";
import Plans from "./pages/subscription/Plans";
import SubscriptionSuccess from "./pages/subscription/Success";
import SubscriptionCancel from "./pages/subscription/Cancel";
import { SubscriptionGuard } from "@/components/subscription/SubscriptionGuard";
import Feedback from "./pages/Feedback";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { FeaturePreviewRouter } from "@/components/subscription/FeaturePreviewRouter";
import AffiliateDashboard from "./pages/affiliate/Dashboard";
import AffiliateApply from "./pages/affiliate/Apply";
import AffiliateTerms from "./pages/affiliate/Terms";
import RequestToJoin from "./pages/affiliate/RequestToJoin";
import Refer from "./pages/Refer";
import ExpiredSubscriptionPage from './pages/ExpiredSubscriptionPage';
import Support from "./pages/Support";
import CurrentAffairsPrelaunch from "./components/current-affairs/CurrentAffairsPrelaunch";

// Improved error handling for lazy-loaded components
const LazyComponentLoader = ({ component: Component, fallback = <LoadingFallback /> }) => {
  return (
    <Suspense fallback={fallback}>
      <ErrorBoundary>
        <Component />
      </ErrorBoundary>
    </Suspense>
  );
};

// Error boundary for catching loading errors
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: Error | null }> {
  state = { hasError: false, error: null };
  
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Component failed to load:', error, info);
    toast.error("Failed to load component. Please try again.");
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 border border-red-300 rounded-md bg-red-50 text-red-800 shadow-sm">
          <h3 className="font-medium mb-2">Something went wrong loading this component</h3>
          <p className="text-sm">Please try refreshing the page. If the issue persists, contact support.</p>
          <button 
            className="mt-4 px-4 py-2 bg-gradient-to-r from-primary to-accent text-white rounded-md text-sm hover:shadow-md transition-all"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    
    return this.props.children;
  }
}

// Fixed lazy imports with more robust error handling and improved fallback component
const createLazyComponent = (importFn, fallbackName) => {
  return React.lazy(() => 
    importFn().catch(error => {
      console.error(`Failed to load ${fallbackName}:`, error);
      return {
        default: () => (
          <div className="p-6 border rounded-lg shadow-sm bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
            <h2 className="text-xl font-bold mb-4 text-red-600">Error Loading {fallbackName}</h2>
            <p>The {fallbackName.toLowerCase()} feature couldn't be loaded. Please try again later.</p>
          </div>
        )
      };
    })
  );
};

const LazyAchievements = createLazyComponent(
  () => import("./components/quiz/AchievementProgress"),
  "Achievements"
);

const LazyAITutoring = createLazyComponent(
  () => import("./components/ai/AITutoring"),
  "AI Tutoring"
);

const LazyVideoLearning = createLazyComponent(
  () => import("./components/video/VideoLearning"),
  "Video Learning"
);

const LazySchedule = createLazyComponent(
  () => import("./components/scheduling/AdaptiveScheduling"),
  "Adaptive Scheduling"
);

const LazyCommunity = createLazyComponent(
  () => import("./components/community/Community"),
  "Community"
);

const LazyCodePlayground = createLazyComponent(
  () => import("./components/learning/CodePlayground"),
  "Code Playground"
);

const LazyAdminDashboard = createLazyComponent(
  () => import("./components/admin/AdminDashboard").then(module => ({
    default: module.AdminDashboard
  })),
  "Admin Dashboard"
);

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-white">
    <div className="text-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
      <p className="text-sm text-gray-500">Loading content...</p>
    </div>
  </div>
);

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log("Auth callback - Processing authentication callback");
        // Check if we have a hash parameter that indicates the type
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);
        
        // Check if this is a recovery (password reset) flow
        const isRecovery = 
          hashParams.get('type') === 'recovery' || 
          searchParams.get('type') === 'recovery';
        
        // If it's a recovery flow, redirect to reset password
        if (isRecovery) {
          console.log("Auth callback - Recovery flow detected, redirecting to reset password");
          navigate('/auth/reset-password', { replace: true });
          return;
        }
        
        // Otherwise, treat it as a signup confirmation flow
        const { error } = await supabase.auth.getSession();
        if (error) throw error;
        
        toast.success("Email verified successfully!");
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error("Failed to verify email. Please try again.");
        navigate('/auth', { replace: true });
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return <LoadingFallback />;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen fullscreen centered withCard={false} message="Verifying your session..." />;
  }
  
  if (!session) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
}

// Create a separate ProtectedRoute that includes SubscriptionGuard
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <PrivateRoute>
      <SubscriptionGuard>
        {children}
      </SubscriptionGuard>
    </PrivateRoute>
  );
}

// Create a separate AdminRoute component that checks admin status
function AdminRoute({ children }: { children: React.ReactNode }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { session } = useAuth();
  const userId = session?.user?.id;

  useEffect(() => {
    async function checkAdminStatus() {
      if (!userId) {
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }
      
      try {
        const { data: isAdminResult, error: adminError } = await supabase
          .rpc('is_admin_by_email');

        if (adminError) {
          console.error('Error checking admin status:', adminError);
          setIsAdmin(false);
        } else {
          setIsAdmin(!!isAdminResult);
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    }
    
    checkAdminStatus();
  }, [userId]);
  
  if (isLoading) {
    return <LoadingFallback />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

// Create a special wrapper for mock test routes
function MockTestRoute({ children }: { children: React.ReactNode }) {
  return (
    <PrivateRoute>
      <SubscriptionGuard>
        <TestSecurityProvider>
          {children}
        </TestSecurityProvider>
      </SubscriptionGuard>
    </PrivateRoute>
  );
}

export default function AppRoutes() {
  const { session, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthRedirects = () => {
      // Check if the current URL is the password reset URL
      if (location.pathname === '/auth/reset-password') {
        // We're already on the reset page, don't redirect
        console.log('Already on reset password page');
        return;
      }
      
      // Check for reset URL parameters
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      
      // Check for code parameter (PKCE flow)
      if (searchParams.has('code')) {
        console.log('Detected auth code parameter');
        // Check if this is a recovery flow or a signup confirmation
        if (searchParams.has('type') && searchParams.get('type') === 'recovery') {
          console.log('Recovery flow detected, redirecting to reset password');
          navigate('/auth/reset-password', { replace: true });
        } else {
          // This is likely a signup confirmation, send to callback handler
          console.log('Signup confirmation flow detected, redirecting to callback');
          navigate('/auth/callback', { replace: true });
        }
        return;
      }
      
      // Check for recovery token (hash format)
      if (window.location.hash && window.location.hash.includes('type=recovery')) {
        console.log('Detected recovery hash parameter, redirecting...');
        navigate('/auth/reset-password', { replace: true });
        return;
      }
      
      // Check for recovery token (query format)
      if (searchParams.has('type') && searchParams.get('type') === 'recovery') {
        console.log('Detected recovery query parameter, redirecting...');
        navigate('/auth/reset-password', { replace: true });
        return;
      }
    };
    
    // Only run the redirect check after the auth state is loaded
    if (!loading) {
      handleAuthRedirects();
    }
  }, [location.pathname, loading, navigate]);

  if (loading) {
    return <LoadingScreen fullscreen centered withCard={false} message="Loading application..." />;
  }

  const mockTestResultsProps = {
    resultsData: null,
    questionTimings: {},
    navigationPatterns: []
  };

  // Public routes that don't require subscription
  const publicRoutes = [
    "/auth",
    "/auth/callback",
    "/auth/reset-password",
    "/privacy-policy",
    "/terms-and-conditions", 
    "/cancellation-policy",
    "/about-us",
    "/contact-us",
    "/pricing",
    "/cookie-policy",
    "/accessibility-statement",
    "/acceptable-use-policy",
    "/gdpr-compliance",
    "/data-processing-agreement",
  ];
  
  const isPublicRoute = publicRoutes.includes(location.pathname) || location.pathname === "/";

  return (
    <SecurityProvider>
      <SecurityMonitoringProvider>
        <Routes>
          {/* Public routes */}
          <Route 
            path="/" 
            element={session ? <Navigate to="/dashboard" replace /> : <LandingPage />} 
          />
          <Route 
            path="/auth" 
            element={session ? <Navigate to="/dashboard" replace /> : <Auth />} 
          />
          <Route 
            path="/auth/callback" 
            element={<AuthCallback />} 
          />
          <Route 
            path="/auth/reset-password" 
            element={<ResetPassword />} 
          />
          
          {/* Referral route */}
          <Route path="/refer/:code" element={<Refer />} />
          
          {/* Public routes that don't need subscription */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
          <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />
          <Route path="/gdpr-compliance" element={<GDPRCompliance />} />
          <Route path="/data-processing-agreement" element={<DataProcessingAgreement />} />
          
          {/* Current Affairs Prelaunch - Public Route */}
          <Route path="/current-affairs" element={<CurrentAffairsPrelaunch />} />
          
          {/* Feature Showcase Route - New */}
          <Route 
            path="/feature-showcase" 
            element={
              <PrivateRoute>
                <FeaturePreviewRouter currentPath={location.pathname} />
              </PrivateRoute>
            } 
          />
          
          {/* Subscription routes */}
          <Route path="/subscription/plans" element={
            <PrivateRoute>
              <Plans />
            </PrivateRoute>
          } />
          <Route path="/subscription/success" element={
            <PrivateRoute>
              <MainLayout>
                <SubscriptionSuccess />
              </MainLayout>
            </PrivateRoute>
          } />
          <Route path="/subscription/cancel" element={
            <PrivateRoute>
              <MainLayout>
                <SubscriptionCancel />
              </MainLayout>
            </PrivateRoute>
          } />
          <Route path="/subscription/expired" element={<ExpiredSubscriptionPage />} />
          
          {/* Affiliate routes */}
          <Route path="/affiliate/dashboard" element={
            <PrivateRoute>
              <MainLayout>
                <AffiliateDashboard />
              </MainLayout>
            </PrivateRoute>
          } />
          <Route path="/affiliate/apply" element={
            <PrivateRoute>
              <MainLayout>
                <AffiliateApply />
              </MainLayout>
            </PrivateRoute>
          } />
          <Route path="/affiliate/request" element={
            <PrivateRoute>
              <MainLayout>
                <RequestToJoin />
              </MainLayout>
            </PrivateRoute>
          } />
          <Route path="/affiliate/terms" element={
            <PrivateRoute>
              <MainLayout>
                <AffiliateTerms />
              </MainLayout>
            </PrivateRoute>
          } />
          
          {/* Protected routes that require subscription - but not enhanced security */}
          <Route element={
            <ProtectedRoute>
              <MainLayout>
                <Outlet />
              </MainLayout>
            </ProtectedRoute>
          }>
            <Route path="/admin" element={
              <AdminRoute>
                <LazyComponentLoader component={LazyAdminDashboard} />
              </AdminRoute>
            } />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/quiz" element={<Quiz />} />
            
            {/* Explicitly define Quiz History routes */}
            <Route path="/quiz/history" element={<QuizHistory />} />
            <Route path="/quiz/results/:sessionId" element={<QuizHistoryDetail />} />
            
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/learning-path" element={<LearningPathVisualization />} />
            <Route path="/flashcards" element={<FlashcardsPage />} />
            <Route path="/achievements" element={
              <LazyComponentLoader component={LazyAchievements} />
            } />
            <Route path="/ai-tutoring" element={
              <LazyComponentLoader component={LazyAITutoring} />
            } />
            <Route path="/video-learning" element={
              <LazyComponentLoader component={LazyVideoLearning} />
            } />
            <Route path="/schedule" element={
              <LazyComponentLoader component={LazySchedule} />
            } />
            <Route path="/community" element={
              <LazyComponentLoader component={LazyCommunity} />
            } />
            <Route path="/playground" element={
              <LazyComponentLoader component={LazyCodePlayground} />
            } />
            <Route path="/study-notes" element={<StudyNotes />} />
            
            {/* Original Current Affairs features - moved to /studio path */}
            <Route path="/current-affairs/studio" element={<CurrentAffairs />} />
            <Route path="/current-affairs/quiz" element={<CurrentAffairsQuiz />} />
            <Route path="/current-affairs/adaptive-quiz" element={<AdaptiveCurrentAffairsQuiz />} />
            <Route path="/current-affairs/quiz-results/:resultId" element={<CurrentAffairsQuizResult />} />
            <Route path="/current-affairs/analytics" element={<CurrentAffairsAnalytics />} />
            <Route path="/current-affairs/quiz-results/detail/:sessionId" element={<CurrentAffairsQuizResultDetail />} />
            
            <Route path="/mock-test-generator" element={<MockTestGenerator />} />
            <Route path="/mock-test-analytics" element={<MockTestAnalytics />} />
            <Route path="/mock-test-word-upload" element={<WordMockTestGeneratorPage />} />
            <Route path="/pyq-multiplier" element={<PYQMultiplierPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/feedback" element={<Feedback />} />
            
            {/* Routes that show mock test results - security not needed */}
            <Route path="/mock-test-results/:resultId" element={<MockTestResults {...mockTestResultsProps} />} />
          </Route>

          {/* Enhanced security routes (mock tests) */}
          <Route element={
            <MockTestRoute>
              <MainLayout>
                <Outlet />
              </MainLayout>
            </MockTestRoute>
          }>
            <Route path="/mock-test/:testId" element={<MockTest />} />
          </Route>
          
          <Route path="/support" element={<Support />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SecurityMonitoringProvider>
    </SecurityProvider>
  );
}
